<template>
  <!-- What each care management program is configured with, one row per program. Starts with summaries; other
       per-program configuration gets its own section here. For whoever can change summary templates. -->
  <div class="p-4 space-y-4">
    <div v-if="!canEdit" class="rounded-lg border border-gray-200 bg-white p-6 text-gray-600" data-test="programs-no-access">
      You need access to edit Hermes templates to see summaries by {{ noun.toLowerCase() }}.
    </div>
    <template v-else>
      <div v-if="error" role="alert" class="rounded-md bg-red-tint px-3 py-2 text-gray-900">{{ error }}</div>
      <div v-else-if="loading" class="text-gray-500">Loading {{ nounPlural.toLowerCase() }}…</div>
      <section v-else class="rounded-lg border border-gray-200 bg-white" data-test="program-summaries">
        <div class="border-b border-gray-200 px-4 py-3">
          <h3 class="font-semibold text-gray-900">Summaries</h3>
          <p class="mt-1 text-gray-600">
            Configure engagement summaries for each {{ noun.toLowerCase() }} and engagement type.
          </p>
        </div>
        <p v-if="!programs.length" class="px-4 py-6 text-gray-600">No care management {{ nounPlural.toLowerCase() }} on this tenant.</p>
        <div v-else class="overflow-x-auto">
          <!-- Fixed layout: the program column, then one equal-width column per summary, so the checks line up. -->
          <table class="w-full table-fixed text-left">
            <colgroup>
              <col class="w-72">
              <col v-for="t in documents" :key="t.id" class="w-44">
            </colgroup>
            <thead>
              <tr class="align-top text-[11px] uppercase tracking-wider text-gray-500">
                <th class="sticky left-0 bg-white px-4 py-2 align-bottom font-medium">{{ noun }}</th>
                <th
                  v-for="t in documents" :key="t.id" class="px-3 py-2 text-center font-medium" :class="columnTone(t)"
                  :data-test="`summary-column-${t.id}`"
                >
                  <!-- An inactive summary's whole column is greyed, so it reads as not in use at a glance. -->
                  <div class="mb-1 text-[10px] font-semibold" :class="t.is_active ? 'text-green-700' : 'text-gray-500'" :data-test="`column-status-${t.id}`">{{ t.is_active ? 'Active' : 'Inactive' }}</div>
                  <!-- Two lines are kept for every name, so the engagement types start at the same height in each column. -->
                  <div class="flex h-10 items-center justify-center">
                    <router-link :to="{ name: 'AdminHermesTemplate', params: { templateId: t.id } }" class="line-clamp-2 normal-case text-sm leading-5 text-primary-700 hover:underline" :title="t.name">
                      {{ t.name }}
                    </router-link>
                  </div>
                  <ul class="mt-1.5 flex flex-col items-center gap-1 normal-case tracking-normal" :data-test="`column-types-${t.id}`">
                    <li
                      v-for="label in engagementTypeLabels(t)" :key="label"
                      class="rounded border px-1.5 py-0.5 text-[11px] text-gray-700"
                      :class="t.is_active ? 'border-neutral-200 bg-neutral-100' : 'border-gray-300 bg-white'"
                    >
                      {{ label }}
                    </li>
                    <li v-if="!engagementTypeLabels(t).length" class="text-[11px] italic text-gray-500">No engagement types</li>
                  </ul>
                </th>
              </tr>
            </thead>
            <!-- Row lines are on the cells, not the rows, so they show through an inactive column's grey. -->
            <tbody>
              <tr v-for="p in programs" :key="p.id" :data-test="`program-row-${p.id}`">
                <td class="sticky left-0 border-t border-gray-100 bg-white px-4 py-2 text-gray-900">
                  <router-link
                    :to="{ name: 'AdminWorkflowMetricWorkflowDetail', params: { metricId: p.id } }"
                    class="text-gray-900 hover:text-primary-700 hover:underline" :data-test="`program-link-${p.id}`"
                  >
                    {{ p.name }}
                  </router-link>
                  <span v-if="!activeCount(p)" class="ml-2 rounded bg-neutral-100 px-1.5 text-gray-900" data-test="program-no-summaries">No summaries</span>
                </td>
                <td v-for="t in documents" :key="t.id" class="border-t px-3 py-2 text-center" :class="[columnTone(t), t.is_active ? 'border-gray-100' : 'border-gray-200 text-gray-400']" :data-test="`cell-${p.id}-${t.id}`">
                  <span v-if="listed(t, p)" class="font-semibold text-gray-900" :title="`This ${noun.toLowerCase()} is in the summary's ${nounPlural.toLowerCase()}`">✓</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';
import { DOCUMENT_KIND, listEngagementTypes, listPrograms, listTemplates } from '@/services/api/hermesTemplates';

// Reading templates needs the Hermes module; changing a summary's programs and engagement types needs template edit,
// the same check the template editor's save uses (HermesViewSet.edit_feature).
export function canEditSummaryTemplates() {
  return canAccessFeature(FEATURES.MODULE_AGENT) && canAccessFeature(FEATURES.ACTION_AGENT_SAVE_ANALYSES);
}

export default {
  name: 'ProgramConfiguration',
  data() {
    return { loading: true, error: '', programs: [], templates: [], engagementTypes: [] };
  },
  computed: {
    canEdit() {
      return canEditSummaryTemplates();
    },
    // What this tenant calls a program (IHCI: opportunity).
    noun() {
      return this.$store?.getters['tenant/opportunityNoun'] || 'Program';
    },
    nounPlural() {
      return this.$store?.getters['tenant/opportunityNounPlural'] || 'Programs';
    },
    // Summary templates: active ones first, then by name.
    documents() {
      return this.templates.filter((t) => t.kind === DOCUMENT_KIND)
        .sort((a, b) => Number(b.is_active) - Number(a.is_active) || a.name.localeCompare(b.name));
    },
    engagementTypeNames() {
      return Object.fromEntries(this.engagementTypes.map((t) => [t.name, t.display_name || t.name]));
    },
  },
  mounted() {
    if (this.canEdit) this.load();
    else this.loading = false;
  },
  methods: {
    async load() {
      try {
        const [programs, templates, engagementTypes] = await Promise.all([listPrograms(), listTemplates(), listEngagementTypes()]);
        this.programs = programs;
        this.templates = templates;
        this.engagementTypes = engagementTypes;
      } catch {
        this.error = 'Programs could not be loaded. Try again in a moment.';
      } finally {
        this.loading = false;
      }
    },
    // A summary applies only to the programs it lists.
    listed(template, program) {
      return (template.metrics || []).includes(program.id);
    },
    activeCount(program) {
      return this.documents.filter((t) => t.is_active && this.listed(t, program) && (t.engagement_types || []).length).length;
    },
    engagementTypeLabels(template) {
      return (template.engagement_types || []).map((name) => this.engagementTypeNames[name] || name);
    },
    columnTone(template) {
      return template.is_active ? '' : 'bg-gray-100/70';
    },
  },
};
</script>
